import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Shield, BarChart3, Zap, CheckCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { getSupabaseLazy } from '../lib/supabase';
import SEO from './SEO';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import AmargiLogo from './AmargiLogo';

/* ─── Types ─────────────────────────────────────────────────────────── */
interface DemoForm {
  name: string;
  email: string;
  company: string;
  size: string;
  message: string;
}

/* ─── Données ────────────────────────────────────────────────────────── */
const STATS = [
  { value: '20 000 €', label: 'Coût moyen d\'un burn-out', source: 'INRS, 2023' },
  { value: '1 sur 3', label: 'Salariés en détresse psychologique', source: 'Malakoff Humanis, 2024' },
  { value: '3,2×', label: 'Plus d\'arrêts chez les dysrégulés', source: 'APA, 2023' },
];

const STEPS = [
  {
    number: '01',
    title: 'Accès privatif',
    text: 'Votre organisation reçoit un lien d\'accès unique. Chaque collaborateur réalise son bilan en 8 minutes, sans créer de compte, sans données nominatives transmises à l\'employeur.',
    icon: Shield,
  },
  {
    number: '02',
    title: 'Tableau de bord agrégé',
    text: 'Vous accédez à un tableau de bord anonymisé : distribution des profils dans votre équipe, score moyen de régulation, zones de tension collective. Aucun profil individuel n\'est visible — seulement les tendances.',
    icon: BarChart3,
  },
  {
    number: '03',
    title: 'Pratiques intégrées',
    text: 'Chaque collaborateur reçoit ses 5 exercices personnalisés. Vous pouvez intégrer des sessions collectives dans vos rituels d\'équipe.',
    icon: Zap,
  },
];

const DONT_LIST = [
  {
    title: 'Nous ne surveillons pas.',
    text: 'Aucun profil individuel n\'est transmis à l\'employeur. Le bilan appartient au collaborateur. La confiance est la condition de l\'efficacité.',
  },
  {
    title: 'Nous ne remplaçons pas le thérapeute.',
    text: 'Amargi est un outil de prévention primaire. Pour les états de détresse sévère, nous orientons vers un professionnel de santé.',
  },
  {
    title: 'Nous ne promettons pas de la performance.',
    text: 'Nous promettons de la régulation. Un système nerveux régulé performe mieux — mais ce n\'est pas notre promesse principale.',
  },
];

const USE_CASES = [
  {
    tag: 'ONBOARDING',
    title: 'Les premières semaines',
    text: 'Intégrez un bilan nerveux dans les premières semaines. Identifiez les nouveaux collaborateurs en zone de stress élevé avant qu\'ils ne décrochent.',
  },
  {
    tag: 'TRANSFORMATION',
    title: 'Périodes de changement',
    text: 'Fusion, restructuration, déménagement. Les changements organisationnels dysrégulent les équipes. Amargi donne aux managers un signal faible mesurable.',
  },
  {
    tag: 'QVT',
    title: 'Programme bien-être',
    text: 'Remplacez les enquêtes annuelles par un outil de mesure continue. Le score de régulation moyen devient un KPI de santé organisationnelle.',
  },
];

const PLANS = [
  {
    name: 'Équipe',
    size: '10 – 50 personnes',
    price: '6',
    unit: '€ / siège / mois',
    highlight: false,
    features: [
      'Accès privatif unique',
      'Tableau de bord agrégé',
      'Support email',
      'Essai gratuit 30 jours',
    ],
  },
  {
    name: 'PME',
    size: '51 – 250 personnes',
    price: '4,50',
    unit: '€ / siège / mois',
    highlight: true,
    badge: 'Le plus choisi',
    features: [
      'Tout le plan Équipe',
      'Sessions collectives guidées (2/mois)',
      'Onboarding dédié',
      'Rapport mensuel',
      'Essai gratuit 30 jours',
    ],
  },
  {
    name: 'Entreprise',
    size: '251 – 1 000 personnes',
    price: '3',
    unit: '€ / siège / mois',
    highlight: false,
    features: [
      'Tout le plan PME',
      'SSO / SAML',
      'Intégration SIRH',
      'Rapport trimestriel',
      'CSM dédié',
    ],
  },
  {
    name: 'Grand Compte',
    size: '1 000+ personnes',
    price: 'Sur devis',
    unit: '',
    highlight: false,
    features: [
      'Tout le plan Entreprise',
      'SLA 99,9 %',
      'Déploiement multi-sites',
      'Formation managers',
      'Contrat sur mesure',
    ],
  },
];

const FAQ = [
  {
    q: 'Les données des collaborateurs sont-elles confidentielles ?',
    a: 'Oui, absolument. L\'employeur n\'a accès qu\'aux données agrégées et anonymisées. Aucun profil individuel n\'est visible. Les données sont stockées sur des serveurs européens, conformément au RGPD.',
  },
  {
    q: 'Combien de temps dure le déploiement ?',
    a: 'Le déploiement standard prend 48 heures. Nous vous fournissons un lien d\'accès privatif, une page de présentation personnalisée et un guide d\'onboarding pour vos collaborateurs.',
  },
  {
    q: 'Faut-il une infrastructure technique particulière ?',
    a: 'Non. Amargi est une application web accessible depuis n\'importe quel navigateur. Aucune installation, aucun plugin, aucune intégration requise pour les plans Équipe et PME.',
  },
  {
    q: 'Comment est calculé le ROI ?',
    a: 'Pour une équipe de 50 personnes, le plan Équipe coûte 3 600 €/an. Un seul burn-out évité représente en moyenne 20 000 € d\'économie (coûts directs + indirects selon l\'INRS). ROI potentiel dès le premier cas évité : 5,5×.',
  },
];

/* ─── Composant ──────────────────────────────────────────────────────── */
export default function OrganisationsPage() {
  const [form, setForm] = useState<DemoForm>({ name: '', email: '', company: '', size: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;
    setStatus('loading');
    try {
      const supabase = await getSupabaseLazy();
      await supabase.from('demo_requests').insert({
        ...form,
        source: 'organisations_page',
        created_at: new Date().toISOString(),
      });
      setStatus('success');
      setForm({ name: '', email: '', company: '', size: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-paper font-sans">
      <SEO
        title="Amargi Organisations — Régulation nerveuse pour vos équipes"
        description="Donnez à chaque collaborateur une lecture précise de son état autonome et les pratiques pour se réguler. Déploiement en 48h, RGPD compliant, tableau de bord agrégé."
        canonical="https://amargis.me/fr/organisations"
      />
      <SiteNav logoVariant="primary" />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-paper pt-32 pb-24 text-center px-6">
        <div className="halo-lapis absolute top-[-120px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] opacity-30 pointer-events-none" />
        <div className="relative max-w-[760px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase mb-5">
            AMARGI ORGANISATIONS
          </p>
          <h1 className="font-serif text-[52px] md:text-[62px] leading-[1.08] text-ink mb-6">
            Votre équipe performe.<br />
            Son système nerveux,<br />
            <span className="text-amber">lui, est à bout.</span>
          </h1>
          <p className="font-sans text-[18px] text-taupe leading-relaxed mb-10 max-w-[560px] mx-auto">
            Amargi donne à chaque collaborateur une lecture précise de son état autonome et les pratiques exactes pour se réguler — en 8 minutes, sans thérapeute, sans stigmatisation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="#demo"
              className="btn-lapis inline-flex items-center gap-2 px-8 py-4 rounded-xl font-sans font-medium text-[15px]"
            >
              Demander une démo — 30 min
              <ArrowRight size={16} />
            </a>
            <a
              href="#tarifs"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-sans font-medium text-[15px] border border-lapis/40 text-lapis hover:bg-lapis-light transition-colors"
            >
              Voir les tarifs organisations
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-[13px] text-taupe/70">
            {['Déploiement en 48h', 'Aucune infrastructure requise', 'RGPD compliant'].map(t => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle size={13} className="text-lapis/60" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────────────────────────── */}
      <section className="bg-limestone/30 py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            LE PROBLÈME
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-12 leading-tight">
            Ce que votre bilan social<br />ne mesure pas encore
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STATS.map(s => (
              <div key={s.label} className="bg-paper rounded-2xl p-8 text-center border border-sand/30 shadow-sm">
                <p className="font-serif text-[44px] text-amber leading-none mb-3">{s.value}</p>
                <p className="font-sans text-[15px] text-ink font-medium mb-2">{s.label}</p>
                <p className="font-sans text-[12px] text-taupe/60">{s.source}</p>
              </div>
            ))}
          </div>
          <p className="font-sans text-[15px] text-taupe text-center mt-10 max-w-[600px] mx-auto leading-relaxed">
            Ces chiffres ne mesurent pas la dysrégulation nerveuse — ils en mesurent les conséquences. Amargi intervient en amont, avant que le système nerveux ne décroche.
          </p>
        </div>
      </section>

      {/* ── Comment ça marche ─────────────────────────────────────────── */}
      <section className="bg-paper py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            DÉPLOIEMENT
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-14 leading-tight">
            Un déploiement en 3 étapes,<br />sans friction
          </h2>
          <div className="space-y-8">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="flex gap-6 items-start bg-limestone/30 rounded-2xl p-8 border border-sand/20">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-lapis-light flex items-center justify-center">
                    <Icon size={22} className="text-lapis" />
                  </div>
                  <div>
                    <p className="font-sans text-[11px] tracking-[0.16em] text-amber uppercase mb-1">{step.number}</p>
                    <h3 className="font-serif text-[22px] text-ink mb-2">{step.title}</h3>
                    <p className="font-sans text-[15px] text-taupe leading-relaxed">{step.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Ce qu'Amargi n'est pas ────────────────────────────────────── */}
      <section className="bg-limestone/30 py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            ÉTHIQUE
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-14 leading-tight">
            Ce que nous ne faisons pas —<br />et pourquoi c'est important
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {DONT_LIST.map((item, i) => (
              <div key={i} className="bg-paper rounded-2xl p-8 border border-sand/30">
                <div className="w-8 h-8 rounded-full bg-lapis/10 flex items-center justify-center mb-4">
                  <Shield size={16} className="text-lapis" />
                </div>
                <h3 className="font-serif text-[18px] text-ink mb-3">{item.title}</h3>
                <p className="font-sans text-[14px] text-taupe leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cas d'usage ───────────────────────────────────────────────── */}
      <section className="bg-paper py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            CAS D'USAGE
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-14 leading-tight">
            Trois contextes où Amargi<br />change la donne
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {USE_CASES.map((uc, i) => (
              <div key={i} className="rounded-2xl p-8 border border-sand/30 bg-limestone/20">
                <p className="font-sans text-[10px] tracking-[0.18em] text-amber uppercase mb-3">{uc.tag}</p>
                <h3 className="font-serif text-[20px] text-ink mb-3">{uc.title}</h3>
                <p className="font-sans text-[14px] text-taupe leading-relaxed">{uc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tarifs ────────────────────────────────────────────────────── */}
      <section id="tarifs" className="bg-limestone/30 py-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            TARIFS
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-4 leading-tight">
            Un investissement, pas un coût
          </h2>
          <p className="font-sans text-[15px] text-taupe text-center mb-14 max-w-[560px] mx-auto">
            Engagement annuel. Facturation mensuelle disponible +15 %. Essai gratuit 30 jours pour les plans Équipe et PME.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((plan, i) => (
              <div
                key={i}
                className={`rounded-2xl p-7 flex flex-col border transition-shadow ${
                  plan.highlight
                    ? 'bg-lapis text-paper border-lapis shadow-[0_8px_40px_rgba(43,95,142,0.28)] scale-[1.02]'
                    : 'bg-paper border-sand/30'
                }`}
              >
                {plan.badge && (
                  <span className="inline-block bg-amber text-paper text-[10px] font-sans font-semibold tracking-[0.12em] uppercase px-3 py-1 rounded-full mb-4 self-start">
                    {plan.badge}
                  </span>
                )}
                <p className={`font-sans text-[11px] tracking-[0.16em] uppercase mb-1 ${plan.highlight ? 'text-paper/60' : 'text-taupe'}`}>
                  {plan.size}
                </p>
                <h3 className={`font-serif text-[24px] mb-3 ${plan.highlight ? 'text-paper' : 'text-ink'}`}>
                  {plan.name}
                </h3>
                <div className="mb-6">
                  <span className={`font-serif text-[36px] leading-none ${plan.highlight ? 'text-paper' : 'text-ink'}`}>
                    {plan.price}
                  </span>
                  {plan.unit && (
                    <span className={`font-sans text-[12px] ml-1 ${plan.highlight ? 'text-paper/60' : 'text-taupe'}`}>
                      {plan.unit}
                    </span>
                  )}
                </div>
                <ul className="space-y-2 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className={`flex items-start gap-2 font-sans text-[13px] ${plan.highlight ? 'text-paper/80' : 'text-taupe'}`}>
                      <CheckCircle size={13} className={`mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-paper/60' : 'text-lapis/60'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#demo"
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-sans font-medium text-[13px] transition-colors ${
                    plan.highlight
                      ? 'bg-paper text-lapis hover:bg-lapis-light'
                      : 'border border-lapis/40 text-lapis hover:bg-lapis-light'
                  }`}
                >
                  {plan.price === 'Sur devis' ? 'Nous contacter' : 'Demander une démo'}
                </a>
              </div>
            ))}
          </div>

          {/* ROI block */}
          <div className="mt-10 bg-amber/8 border border-amber/20 rounded-2xl p-8 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-amber/15 flex items-center justify-center">
              <BarChart3 size={26} className="text-amber" />
            </div>
            <div>
              <p className="font-sans text-[11px] tracking-[0.16em] text-amber uppercase mb-1">CALCUL ROI</p>
              <p className="font-serif text-[18px] text-ink mb-1">
                Pour 50 personnes : 3 600 €/an. Un burn-out évité : 20 000 €.
              </p>
              <p className="font-sans text-[14px] text-taupe">
                ROI potentiel dès le premier cas évité : <strong className="text-ink">5,5×</strong>. Source : INRS, 2023.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="bg-paper py-24 px-6">
        <div className="max-w-[600px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            FAQ
          </p>
          <h2 className="font-serif text-[32px] text-ink text-center mb-12 leading-tight">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="border border-sand/40 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-limestone/30 transition-colors"
                >
                  <span className="font-sans font-medium text-[15px] text-ink">{item.q}</span>
                  {openFaq === i
                    ? <ChevronUp size={16} className="text-taupe flex-shrink-0" />
                    : <ChevronDown size={16} className="text-taupe flex-shrink-0" />
                  }
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 font-sans text-[14px] text-taupe leading-relaxed border-t border-sand/30 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Formulaire démo ───────────────────────────────────────────── */}
      <section id="demo" className="bg-limestone/30 py-24 px-6">
        <div className="max-w-[560px] mx-auto">
          <p className="font-sans text-[11px] tracking-[0.18em] text-amber uppercase text-center mb-3">
            DÉMO GRATUITE
          </p>
          <h2 className="font-serif text-[32px] md:text-[38px] text-ink text-center mb-3 leading-tight">
            Parlons de votre équipe
          </h2>
          <p className="font-sans text-[15px] text-taupe text-center mb-10">
            30 minutes. Pas de pitch commercial. On regarde ensemble si Amargi est pertinent pour votre contexte.
          </p>

          {status === 'success' ? (
            <div className="bg-paper rounded-2xl p-10 text-center border border-sand/30">
              <CheckCircle size={40} className="text-lapis mx-auto mb-4" />
              <h3 className="font-serif text-[24px] text-ink mb-2">Demande reçue.</h3>
              <p className="font-sans text-[15px] text-taupe">
                Nous vous recontactons sous 24h ouvrées pour planifier la démo.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-paper rounded-2xl p-8 border border-sand/30 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-sans text-[12px] text-taupe mb-1.5 uppercase tracking-wide">Prénom et nom *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-sand/50 rounded-xl px-4 py-3 font-sans text-[14px] text-ink bg-paper focus:outline-none focus:border-lapis/60 focus:ring-1 focus:ring-lapis/20 transition"
                    placeholder="Marie Dupont"
                  />
                </div>
                <div>
                  <label className="block font-sans text-[12px] text-taupe mb-1.5 uppercase tracking-wide">Email professionnel *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-sand/50 rounded-xl px-4 py-3 font-sans text-[14px] text-ink bg-paper focus:outline-none focus:border-lapis/60 focus:ring-1 focus:ring-lapis/20 transition"
                    placeholder="marie@entreprise.com"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-sans text-[12px] text-taupe mb-1.5 uppercase tracking-wide">Entreprise *</label>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                    className="w-full border border-sand/50 rounded-xl px-4 py-3 font-sans text-[14px] text-ink bg-paper focus:outline-none focus:border-lapis/60 focus:ring-1 focus:ring-lapis/20 transition"
                    placeholder="Nom de votre organisation"
                  />
                </div>
                <div>
                  <label className="block font-sans text-[12px] text-taupe mb-1.5 uppercase tracking-wide">Taille de l'équipe</label>
                  <select
                    value={form.size}
                    onChange={e => setForm(f => ({ ...f, size: e.target.value }))}
                    className="w-full border border-sand/50 rounded-xl px-4 py-3 font-sans text-[14px] text-ink bg-paper focus:outline-none focus:border-lapis/60 focus:ring-1 focus:ring-lapis/20 transition"
                  >
                    <option value="">Sélectionner</option>
                    <option value="10-50">10 – 50 personnes</option>
                    <option value="51-250">51 – 250 personnes</option>
                    <option value="251-1000">251 – 1 000 personnes</option>
                    <option value="1000+">1 000+ personnes</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-sans text-[12px] text-taupe mb-1.5 uppercase tracking-wide">Contexte (optionnel)</label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full border border-sand/50 rounded-xl px-4 py-3 font-sans text-[14px] text-ink bg-paper focus:outline-none focus:border-lapis/60 focus:ring-1 focus:ring-lapis/20 transition resize-none"
                  placeholder="Quel est votre contexte ? Burn-out, restructuration, programme QVT..."
                />
              </div>
              {status === 'error' && (
                <p className="font-sans text-[13px] text-red-500">Une erreur est survenue. Merci de réessayer.</p>
              )}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="btn-lapis w-full py-4 rounded-xl font-sans font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status === 'loading' ? 'Envoi en cours...' : (
                  <>Réserver ma démo gratuite <ArrowRight size={16} /></>
                )}
              </button>
              <p className="font-sans text-[12px] text-taupe/60 text-center">
                Réponse sous 24h ouvrées. Aucun engagement.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────────────── */}
      <section className="bg-ink py-24 px-6 text-center">
        <div className="max-w-[600px] mx-auto">
          <div className="flex justify-center mb-6">
            <AmargiLogo variant="light" className="h-8" />
          </div>
          <h2 className="font-serif text-[32px] md:text-[40px] text-paper mb-4 leading-tight">
            Votre prochaine réunion d'équipe<br />
            <span className="text-amber">pourrait commencer par un bilan nerveux.</span>
          </h2>
          <p className="font-sans text-[16px] text-paper/60 mb-10">
            Démo de 30 minutes. Déploiement en 48h. Aucune infrastructure requise.
          </p>
          <a
            href="#demo"
            className="btn-lapis inline-flex items-center gap-2 px-10 py-4 rounded-xl font-sans font-medium text-[16px]"
          >
            Réserver une démo — c'est gratuit
            <ArrowRight size={16} />
          </a>
          <p className="font-sans text-[13px] text-paper/30 mt-6">
            Ou écrivez-nous à{' '}
            <a href="mailto:organisations@amargis.me" className="text-paper/50 hover:text-paper/70 underline">
              organisations@amargis.me
            </a>
          </p>
        </div>
      </section>
      <BlogFooter />
    </div>
  );
}
